import React from 'react';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';
import { withTheme } from 'styled-components';
import Icon from '../icons/Icon';
import IconBack from '../icons/IconBack';
import AlertModal from '../shared/AlertModal';
import { extractAttendeeIdFromQR } from '../../helpers/qrCode';

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: transparent;
  z-index: 1000;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  position: relative;
  min-height: 50px;
  background-color: #fff;
  display: flex;
  align-items: center;
  padding: 0;
  z-index: 15;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin: 5px 0 5px 15px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ScanArea = styled.div`
  flex: 1;
  position: relative;
  background-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ScanFrame = styled.div`
  position: relative;
  width: 250px;
  height: 250px;
  border: 2px solid #666;
  background: transparent;
`;

const CornerBracket = styled.div`
  position: absolute;
  width: 30px;
  height: 30px;
  border: 3px solid #ff0000;
  ${props => {
    switch (props.position) {
      case 'top-left':
        return `
          top: -3px;
          left: -3px;
          border-right: none;
          border-bottom: none;
        `;
      case 'top-right':
        return `
          top: -3px;
          right: -3px;
          border-left: none;
          border-bottom: none;
        `;
      case 'bottom-left':
        return `
          bottom: -3px;
          left: -3px;
          border-right: none;
          border-top: none;
        `;
      case 'bottom-right':
        return `
          bottom: -3px;
          right: -3px;
          border-left: none;
          border-top: none;
        `;
      default:
        return '';
    }
  }}
`;

const ErrorMessage = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #fff;
  text-align: center;
  background-color: rgba(0, 0, 0, 0.8);
  padding: 20px;
  border-radius: 8px;
  max-width: 300px;
`;

class QRCodeScanner extends React.Component {
  state = {
    error: null,
    hasPermission: false,
    showErrorModal: false
  };

  componentDidMount() {
    // Make body and html transparent to show camera
    document.body.classList.add('scanner-active');
    document.documentElement.classList.add('scanner-active');
    this.initializeScanner();
  }

  componentWillUnmount() {
    // Restore body and html opacity
    document.body.classList.remove('scanner-active');
    document.documentElement.classList.remove('scanner-active');
    this.stopScanner();
  }

  initializeScanner = () => {
    if (window.cordova && window.QRScanner) {
      console.log('QR Scanner plugin found, starting scanner...');
      this.startScanner();
    } else {
      // Plugin not available - show error
      console.error('QR Scanner plugin not available');
      this.setState({ 
        error: this.props.t('qr_scanner_not_available'),
        isScanning: false 
      });
    }
  };

  startScanner = () => {
    try {
      const qrscanner = window.QRScanner;
      
      if (!qrscanner) {
        throw new Error('QR Scanner plugin not found');
      }
      
      qrscanner.prepare((err, status) => {
        if (err) {
          console.error('QR Scanner prepare error:', err);
          this.setState({ 
            error: this.props.t('qr_scanner_prepare_error'),
            isScanning: false 
          });
          return;
        }

        if (status.authorized) {
          this.setState({ hasPermission: true });

          qrscanner.show((status) => {
            // Scanner is now visible, start scanning
            qrscanner.scan((err, result) => {
              if (err) {
                if (err.name === 'SCAN_CANCELED') {
                  // User canceled scan
                  this.props.onClose();
                } else {
                  console.error('QR Scanner scan error:', err);
                  this.setState({
                    error: this.props.t('qr_scanner_scan_error'),
                    isScanning: false
                  });
                }
                return;
              }

              if (result) {
                this.handleScanResult(result);
              }
            });
          });

        } else if (status.denied) {
          this.setState({ 
            error: this.props.t('qr_scanner_permission_denied'),
            isScanning: false 
          });
        } else {
          this.setState({ 
            error: this.props.t('qr_scanner_permission_required'),
            isScanning: false 
          });
        }
      });
    } catch (error) {
      console.error('QR Scanner initialization error:', error);
      this.setState({ 
        error: this.props.t('qr_scanner_not_available'),
        isScanning: false 
      });
    }
  };

  stopScanner = () => {
    if (window.cordova && window.QRScanner) {
      window.QRScanner.destroy();
    }
  };

  handleScanResult = (result) => {
    // Remove scanner-active classes to restore page visibility
    document.body.classList.remove('scanner-active');
    document.documentElement.classList.remove('scanner-active');
    this.stopScanner();

    // Use existing logic to extract attendee ID
    const attendeeId = extractAttendeeIdFromQR(result);

    if (attendeeId) {
      this.props.onScan(result);
    } else {
      this.setState({ showErrorModal: true });
    }
  };

  handleBackClick = () => {
    document.body.classList.remove('scanner-active');
    document.documentElement.classList.remove('scanner-active');
    this.stopScanner();
    this.props.onClose();
  };

  handleCloseErrorModal = () => {
    this.setState({ showErrorModal: false });
    document.body.classList.remove('scanner-active');
    document.documentElement.classList.remove('scanner-active');
    this.stopScanner();
    this.props.onClose();
  };

  handleTryAgain = () => {
    this.setState({ showErrorModal: false });
    document.body.classList.add('scanner-active');
    document.documentElement.classList.add('scanner-active');
    this.startScanner();
  };

  render() {
    const { t, theme } = this.props;
    const { error, showErrorModal } = this.state;

    return (
      <Container className="qr-scanner-overlay">
        <Header>
          <BackButton onClick={this.handleBackClick}>
            <Icon
              icon={IconBack}
              width={'40px'}
              height={'40px'}
              padding={'10px 20px 10px 0'}
              color={theme.mainColor}
            />
          </BackButton>
        </Header>

        <ScanArea>
          {error ? (
            <ErrorMessage>
              {error}
            </ErrorMessage>
          ) : (
            <ScanFrame>
              <CornerBracket position="top-left" />
              <CornerBracket position="top-right" />
              <CornerBracket position="bottom-left" />
              <CornerBracket position="bottom-right" />
            </ScanFrame>
          )}
        </ScanArea>

        <AlertModal
          open={showErrorModal}
          onClose={this.handleCloseErrorModal}
          onButtonClick={this.handleTryAgain}
          theme={theme}
          message={t('qr_code_not_recognized')}
          buttonText={t('try_again')}
        />
      </Container>
    );
  }
}

export default withTranslation()(withTheme(QRCodeScanner));
